/* eslint-disable @typescript-eslint/consistent-type-assertions */
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  DatePickerProps,
  Input,
  Select,
  Typography,
  message,
} from 'antd';
import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './index.scss';
import SenderSelectorComponent from '../../components/SenderSelector';
import AnnouncementRecipientSelector from '../../components/AnnouncementRecipientSelector';
import { MailApiSendAnnouncementRequestBody } from '../../services/api/types/mail';
import { apiMailSendAnnouncement } from '../../services/api/mail';
import { useSelector } from 'react-redux';
import { selectUserState } from '../../store/selectors/user';
import { Editor } from '@monaco-editor/react';

export default function SendAnnoouncementPage() {
  const [type, setType] = React.useState<'markdown' | 'html'>('markdown');
  const { token } = useSelector(selectUserState);
  const [loading, setLoading] = React.useState(false);
  const [scheduleEnabled, setScheduleEnabled] = React.useState(false);
  const [body, setBody] = React.useState<{
    account: string | null;
    subject: string | null;
    content: string | null;
    scheduleAt: Date | null;
    recipients: {
      email: string;
      name: string;
    }[];
  }>({
    account: null,
    subject: null,
    content: null,
    recipients: [],
    scheduleAt: null,
  });

  function checkIfValid() {
    if (loading) {
      return false;
    }
    if (!body.account) {
      return false;
    }
    if (!body.subject) {
      return false;
    }
    if (!body.content) {
      return false;
    }
    if (!body.recipients.length) {
      return false;
    }
    return true;
  }

  function onOk(value: DatePickerProps['value']) {
    console.log(value?.toDate());
    setBody({
      ...body,
      scheduleAt: value?.toDate() || null,
    });
  }

  async function submit() {
    if (checkIfValid() && token) {
      setLoading(true);
      let content = body.content as string;
      if (type === 'markdown') {
        while (content.includes(`class="ql-size-small"`)) {
          content = content.replace(
            `class="ql-size-small"`,
            `style="font-size: 0.75em;"`,
          );
        }
        while (content.includes(`class="ql-size-large"`)) {
          content = content.replace(
            `class="ql-size-large"`,
            `style="font-size: 1.5em;"`,
          );
        }
        while (content.includes(`class="ql-size-huge"`)) {
          content = content.replace(
            `class="ql-size-huge"`,
            `style="font-size: 2.5em;"`,
          );
        }
      }
      const res = await apiMailSendAnnouncement(
        { ...body, content } as MailApiSendAnnouncementRequestBody,
        token,
      );
      if (res?.data.success) {
        message.success('Successfully sent announcement!');
      } else {
        message.error('Error while sending announcement!');
      }
      setLoading(false);
    }
  }

  async function submitTest() {
    if (!token) {
      return;
    }
    const account = 'no-reply@tapform.io';
    const subject = `[TEST] ${body.subject || 'Test Subject'}`;
    const content = body.content || 'This is a test email content.';
    const recipients = [{ email: 'matej@tapform.io', name: 'Matej Rajcic' }];

    await apiMailSendAnnouncement(
      {
        account,
        subject,
        content,
        recipients,
        scheduleAt: null,
      } as MailApiSendAnnouncementRequestBody,
      token,
    );
  }

  return (
    <Card bordered={false} title="Send Announcements">
      <Typography.Title level={2}>Send Announcement</Typography.Title>
      <ul style={{ padding: '0 25px 55px' }}>
        <span>You can use:</span>
        <li>
          <strong>{`{{name}}`}</strong> - Injects recipient full name
        </li>
        <li>
          <strong>{`{{email}}`}</strong> - Injects recipient email
        </li>
      </ul>
      <div style={{ marginBottom: 15 }}>
        <Select
          style={{ width: 200, marginRight: 15 }}
          value={type}
          onChange={(value) => {
            setType(value);
          }}
        >
          <Select.Option value="markdown">Markdown</Select.Option>
          <Select.Option value="html">HTML</Select.Option>
        </Select>
      </div>
      <div style={{ marginBottom: 15 }}>
        <SenderSelectorComponent
          onResult={(sender) => {
            setBody({ ...body, account: sender });
          }}
          style={{ marginRight: 15 }}
        />
        <AnnouncementRecipientSelector
          onResult={(recipients) => {
            setBody({
              ...body,
              recipients: recipients.map(({ email, name }) => ({
                email,
                name,
              })),
            });
          }}
          style={{ marginRight: 35 }}
        />
        <div style={{ marginRight: 35, display: 'inline' }}>
          <Checkbox
            onChange={(val) => {
              if (val.target.checked) {
                setScheduleEnabled(true);
              } else {
                setScheduleEnabled(false);
                setBody({
                  ...body,
                  scheduleAt: null,
                });
              }
            }}
          >
            Schedule
          </Checkbox>
          {scheduleEnabled ? <DatePicker showTime onChange={onOk} /> : null}
        </div>
        <Button
          type="primary"
          disabled={!checkIfValid()}
          loading={loading}
          onClick={submit}
          style={{ marginRight: 15 }}
        >
          Send
        </Button>
        <Button type="dashed" loading={loading} onClick={submitTest}>
          Send test
        </Button>
      </div>
      <div style={{ marginBottom: 15 }}>
        <Input
          placeholder="Enter subject"
          type="text"
          onChange={(e) => {
            setBody({ ...body, subject: e.target.value?.trim() });
          }}
        />
      </div>
      {type === 'markdown' ? (
        <ReactQuill
          theme="snow"
          defaultValue={body.content || ''}
          modules={{
            toolbar: [
              [{ header: '1' }, { header: '2' }, { font: [] }],
              [{ size: [] }],
              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
              [
                { list: 'ordered' },
                { list: 'bullet' },
                { indent: '-1' },
                { indent: '+1' },
              ],
              ['link'],
              ['clean'],
            ],
            clipboard: {
              // toggle to add extra line breaks when pasting HTML:
              matchVisual: false,
            },
          }}
          onChange={(content) => {
            setBody({
              ...body,
              content,
            });
          }}
        />
      ) : (
        <Editor
          height="800px"
          defaultLanguage="html"
          defaultValue={body.content || ''}
          onChange={(value) => {
            setBody({
              ...body,
              content: value || '',
            });
          }}
          theme="vs-dark"
        />
      )}
    </Card>
  );
}
